
import React, { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Conta } from "@/types/database";
import { useAuth } from "@/hooks/useAuth";
import { ArrowRightLeft } from "lucide-react";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contas: Conta[];
  onAfterTransfer: () => void;
};

export default function TransferModal({ open, onOpenChange, contas, onAfterTransfer }: Props) {

  const { user } = useAuth();
  const [origem, setOrigem] = useState<string>("");
  const [destino, setDestino] = useState<string>("");
  const [valor, setValor] = useState<string | number>("");
  const [data, setData] = useState<string>(new Date().toISOString().slice(0, 10));
  const [descricao, setDescricao] = useState<string>("");

  const contasOrdenadas = useMemo(() => {
    return [...contas]
      .filter(c => c.tipo !== "cartao_credito")
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }, [contas]);

  const canSubmit = origem && destino && origem !== destino && Number(valor) > 0;

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.error("Preencha origem, destino diferentes e valor > 0.");
      return;
    }

    if (!user?.id) {
      toast.error("Usuário não autenticado.");
      return;
    }

    const { error } = await supabase.from("transferencias").insert({
      conta_origem_id: origem,
      conta_destino_id: destino,
      valor: Number(valor),
      data,
      descricao: descricao || null,
      user_id: user.id,
    });

    if (error) {
      console.error("Erro ao transferir:", error);
      toast.error("Não foi possível concluir a transferência.");
      return;
    }

    toast.success("Transferência realizada - Saldos atualizados.");
    onOpenChange(false);
    setOrigem("");
    setDestino("");
    setValor("");
    setDescricao("");
    onAfterTransfer();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="w-5 h-5 text-purple-600" />
            Nova transferência
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Conta de origem</Label>
            <Select value={origem} onValueChange={setOrigem}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                {contasOrdenadas
                  .filter(c => c.id !== destino)
                  .map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Conta de destino</Label>
            <Select value={destino} onValueChange={setDestino}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                {contasOrdenadas
                  .filter(c => c.id !== origem)
                  .map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Valor</Label>
              <CurrencyInput
                value={valor}
                onChange={setValor}
                placeholder="0,00"
              />
            </div>
            <div>
              <Label>Data</Label>
              <DatePicker
                value={data}
                onChange={setData}
                placeholder="Selecione a data"
              />
            </div>
          </div>
          <div>
            <Label>Descrição (opcional)</Label>
            <Input 
              value={descricao} 
              onChange={(e) => setDescricao(e.target.value)} 
              placeholder="Ex.: transferência entre contas" 
            />
          </div>
          <div className="mt-4">
            <Button 
              onClick={handleSubmit} 
              disabled={!canSubmit}
              className="w-full h-11 rounded-lg text-white font-medium bg-purple-600 hover:bg-purple-700"
            >
              Transferir
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
